import { Injectable } from '@angular/core';
import { CompanyDomainModel } from '../../../../domainModels/company-domain-model';
import { LabelDomainModel } from '../../../../domainModels/label-domain-model';
import { LocationDomainModel } from '../../../../domainModels/location-domain-model';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject, combineLatest, fromEvent, Observable, of, Subscription } from 'rxjs';
import { Menu } from '../../../../models/menu/dto/menu';
import { delay, distinctUntilChanged, filter, map, shareReplay } from 'rxjs/operators';
import { exists } from '../../../../functions/exists';
import { environment } from '../../../../../environments/environment';
import { StringifyUtils } from '../../../../utils/stringify-utils';
import { LoadingOptions } from '../../../../models/shared/loading-options';
import { LoadingSpinnerSize } from '../../../../models/enum/shared/loading-spinner-size.enum';
import { LiveViewViewModel } from '../../shared/live-view-view-model';
import { DisplayAttributesDomainModel } from '../../../../domainModels/display-attributes-domain-model';

@Injectable()
export class PrintMenuLiveViewViewModel extends LiveViewViewModel {
  constructor(
    companyDomainModel: CompanyDomainModel,
    displayAttributesDomainModel: DisplayAttributesDomainModel,
    labelDomainModel: LabelDomainModel,
    locationDomainModel: LocationDomainModel,
    sanitizer: DomSanitizer
  ) {
    super(companyDomainModel, displayAttributesDomainModel, labelDomainModel, locationDomainModel, sanitizer);
    this.receivePageInfoFromIFrame();
    this.loadingSpinner();
  }

  private sendDataSub: Subscription | null = null;

  protected override readonly _loadingOpts = new BehaviorSubject(this.getLoadingOpts());
  private readonly borderInPx$ = of(0);
  public readonly url$ = of(this.generateLiveViewURL());

  private readonly _printMenu = new BehaviorSubject<Menu | null>(null);
  private readonly printMenuSource$ = this._printMenu as Observable<Menu | null>;
  public readonly printMenu$ = combineLatest([
    this.printMenuSource$.deepCopy$(),
    this.locationDisplayAttributesIndex$,
    this.companyDisplayAttributesIndex$,
    this.companyConfig$
  ]).pipe(
    map(([printMenu, locationDAIndex, companyDAIndex, companyConfig]) => {
      return printMenu?.adjustDataForLiveView(locationDAIndex, companyDAIndex, companyConfig);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  connectToPrintMenu = (printMenu: Menu | null) => this._printMenu.next(printMenu);

  /* *************************** Local Threads of Execution *************************** */

  private loadCompanyDisplayAttributesForLiveViewMenu = this.printMenuSource$.notNull().subscribeWhileAlive({
    owner: this,
    next: menu => this.loadCompanyDisplayAttributesForMenu(menu)
  });

  /* ********************************************************************************** */

  private readonly _menuHeight = new BehaviorSubject<number | null>(0);
  public readonly menuHeight$ = combineLatest([this.borderInPx$, this._menuHeight]).pipe(
    filter(([_, menuHeight]) => exists(menuHeight)),
    map(([borderInPx, menuHeight]) => menuHeight + borderInPx * 2),
    distinctUntilChanged()
  );
  connectToMenuHeight = (menuHeight: number | null) => this._menuHeight.next(menuHeight);

  private readonly _menuWidth = new BehaviorSubject<number | null>(0);
  public readonly menuWidth$ = combineLatest([this.borderInPx$, this._menuWidth]).pipe(
    filter(([_, menuWidth]) => exists(menuWidth)),
    map(([borderInPx, menuWidth]) => menuWidth + borderInPx * 2),
    distinctUntilChanged()
  );
  connectToMenuWidth = (menuWidth: number | null) => this._menuWidth.next(menuWidth);

  private readonly _nPages = new BehaviorSubject<number>(0);
  public readonly nPages$ = this._nPages.pipe(distinctUntilChanged());
  connectToNPages = (nPages: number) => this._nPages.next(nPages);

  public readonly iFrameIsVisible$ = this.menuHeight$.pipe(
    map(height => height > 0),
    delay(250)
  );

  public readonly boxShadow$ = this.menuHeight$.pipe(map(height => (exists(height) ? '1px 1px 15px black' : null)));

  public readonly scaleToParentView$ = combineLatest([
    this.menuHeight$,
    this.menuWidth$,
    this.parentContainerHeight$,
    this.parentContainerWidth$
  ]).pipe(
    map(([menuHeight, menuWidth, parentContainerHeight, parentContainerWidth]) => {
      const heightRatio = parentContainerHeight && menuHeight ? parentContainerHeight / menuHeight : 1;
      const widthRatio = parentContainerWidth && menuWidth ? parentContainerWidth / menuWidth : 1;
      return Math.min(heightRatio, widthRatio, 1);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  private getLoadingOpts(): LoadingOptions {
    const opts = LoadingOptions.defaultWhiteBackground();
    opts.spinnerSize = LoadingSpinnerSize.Large;
    return opts;
  }

  protected override generateLiveViewURL(): SafeResourceUrl {
    const cacheBuster = '?cacheBuster=' + Date.now() + Math.floor(Math.random() * 1000000);
    const env = environment.production && environment.environmentName !== 'rc' ? '' : `${environment.environmentName}.`;
    const url = `https://${env}display.mybudsense.com/#/print/live-view/${cacheBuster}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  public override sendDataToIFrame(iFrame: HTMLIFrameElement): void {
    this.sendDataSub?.unsubscribe();
    this.sendDataSub = combineLatest([
      this.locationConfig$.notNull(),
      this.companyConfig$.notNull(),
      this.labels$.notNull(),
      this.printMenu$.notNull()
    ]).subscribeWhileAlive({
      owner: this,
      next: ([locationConfig, companyConfig, labels, printMenu]) => {
        const msg = {
          type: 'menu',
          locationConfig: JSON.parse(JSON.stringify(locationConfig, StringifyUtils.displayAppReplacer)),
          companyConfig: JSON.parse(JSON.stringify(companyConfig, StringifyUtils.displayAppReplacer)),
          labels: JSON.parse(JSON.stringify(labels, StringifyUtils.displayAppReplacer)),
          printMenu: JSON.parse(JSON.stringify(printMenu, StringifyUtils.displayAppReplacer))
        };
        iFrame?.contentWindow?.postMessage(msg, '*');
      }
    });
  }

  public sendPageSelectionToIFrame(iFrame: HTMLIFrameElement, pageIndex: number): void {
    const msg = { type: 'page', pageIndex };
    iFrame?.contentWindow?.postMessage(msg, '*');
  }

  /**
   * All messages coming from window get intercepted here. This means messages that the dashboard app sends
   * to the display app will be received here as well. Therefore, we must check if the specific message contains
   * relevant data, or else we can end up in an infinite loop.
   */
  private receivePageInfoFromIFrame(): void {
    fromEvent(window, 'message')
      .pipe(
        filter((event: MessageEvent) => {
          const origin = event?.origin?.toLowerCase();
          const hasData = event?.data?.type === 'page';
          return hasData && (origin?.includes('localhost') || origin?.includes('mybudsense.com'));
        }),
        map((event: MessageEvent) => event?.data)
      )
      .subscribeWhileAlive({
        owner: this,
        next: (msg: any) => {
          if (msg?.menuHeight) this.connectToMenuHeight(msg?.menuHeight);
          if (msg?.menuWidth) this.connectToMenuWidth(msg?.menuWidth);
          if (msg?.nPages) this.connectToNPages(msg?.nPages);
        }
      });
  }

  private loadingSpinner(): void {
    combineLatest([this.menuHeight$, this.nPages$]).subscribeWhileAlive({
      owner: this,
      next: ([menuHeight, nPages]) => {
        if (menuHeight > 0 && !this._loadingOpts.containsRequest('')) this._loadingOpts.addRequest('');
        if (menuHeight > 0 && nPages > 0) this._loadingOpts.removeRequest('');
      }
    });
  }
}
